import { buildEndpointProbePayloads } from "./probePayloadUtils";
import { normalizeEndpointsFromForm } from "./gate0Storage";

function utf8ToBase64(str) {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
  );
}

function basicAuthHeader(email, apiToken) {
  return `Basic ${utf8ToBase64(`${email}:${apiToken}`)}`;
}

/**
 * @param {object} form — Gate0IntakePage form state
 * @param {{ bearerToken?: string, basicEmail?: string, basicApiToken?: string, apiKeyHeader?: string, apiKeyValue?: string }} credentials
 * @param {{ endpointIds?: string[] }} [options]
 * @returns {{ app_config: object } | { error: string }}
 */
export function buildAppConfigFromGate0Form(form, credentials = {}, options = {}) {
  const baseUrl = String(form.baseUrl || "").trim().replace(/\/+$/, "");

  if (!baseUrl) {
    return { error: "Enter an API base URL before testing endpoints." };
  }
  if (!/^https?:\/\//i.test(baseUrl)) {
    return { error: "Base URL must start with https:// or http://" };
  }

  let endpoints = normalizeEndpointsFromForm(form);
  if (options.endpointIds?.length) {
    const idSet = new Set(options.endpointIds.map(String));
    endpoints = endpoints.filter((ep) => idSet.has(String(ep.id)));
  }

  if (endpoints.length === 0) {
    return { error: "Add at least one endpoint path before running API tests." };
  }

  const bearer = String(credentials.bearerToken || "").trim();
  const basicEmail = String(credentials.basicEmail || "").trim();
  const basicTok = String(credentials.basicApiToken || "").trim();
  const kHeader = String(credentials.apiKeyHeader || "").trim();
  const kVal = String(credentials.apiKeyValue || "").trim();
  const authType = String(form.authType || "").toLowerCase();

  let authConfigs = {};
  let defaultAuthRef = null;

  const useBearer =
    authType.includes("bearer") && bearer
      ? true
      : !authType.includes("basic") &&
        !authType.includes("api key") &&
        bearer &&
        !(basicEmail && basicTok);

  const useBasic = authType.includes("basic") && basicEmail && basicTok;
  const useApiKey = authType.includes("api key") && kHeader && kVal;

  if (useBearer) {
    authConfigs["auth-main"] = {
      type: "bearer_jwt",
      header: "Authorization",
      token: bearer,
    };
    defaultAuthRef = "auth-main";
  } else if (useBasic) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: "Authorization",
      value: basicAuthHeader(basicEmail, basicTok),
    };
    defaultAuthRef = "auth-main";
  } else if (useApiKey) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: kHeader,
      value: kVal,
    };
    defaultAuthRef = "auth-main";
  } else if (bearer) {
    authConfigs["auth-main"] = {
      type: "bearer_jwt",
      header: "Authorization",
      token: bearer,
    };
    defaultAuthRef = "auth-main";
  } else if (basicEmail && basicTok) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: "Authorization",
      value: basicAuthHeader(basicEmail, basicTok),
    };
    defaultAuthRef = "auth-main";
  } else if (kHeader && kVal) {
    authConfigs["auth-main"] = {
      type: "api_key",
      header: kHeader,
      value: kVal,
    };
    defaultAuthRef = "auth-main";
  } else if (!authType || authType.includes("none") || authType === "") {
    authConfigs["auth-none"] = { type: "none" };
    defaultAuthRef = "auth-none";
  } else if (authType.includes("public")) {
    authConfigs["auth-none"] = { type: "none" };
    defaultAuthRef = "auth-none";
  } else {
    return {
      error:
        "Provide credentials (Basic email + API token, Bearer, or API key) to test authenticated endpoints.",
    };
  }

  const formEndpoints = (form.endpoints || []).filter((ep) =>
    endpoints.some((e) => e.id === ep.id)
  );

  const mappedEndpoints = [];
  for (let i = 0; i < endpoints.length; i++) {
    const e = endpoints[i];
    const formEp = formEndpoints.find((fe) => fe.id === e.id) || {};
    const payloadCfg = buildEndpointProbePayloads({ ...formEp, method: e.method });
    if (payloadCfg.error) {
      return { error: `${e.path}: ${payloadCfg.error}` };
    }

    mappedEndpoints.push({
      id: e.id || `ep-${i + 1}`,
      method: e.method,
      path: e.path,
      operation: e.operation || "",
      purpose: e.purpose || e.operation || `Endpoint ${i + 1}`,
      auth_ref: defaultAuthRef,
      request_schema: {},
      response_schema: {},
      test_data_source: { type: "synthetic" },
      risk_scope: [],
      ...(payloadCfg.payloads ? { payloads: payloadCfg.payloads } : {}),
      ...(payloadCfg.request_body !== undefined ? { request_body: payloadCfg.request_body } : {}),
      ...(e.prompt_field ? { prompt_field: e.prompt_field } : {}),
      ...(e.response_field ? { response_field: e.response_field } : {}),
      ...(formEp.prompt_field ? { prompt_field: formEp.prompt_field } : {}),
      ...(formEp.response_field ? { response_field: formEp.response_field } : {}),
    });
  }

  const app_config = {
    app_id: `gate0-draft-${Date.now()}`,
    app_name: String(form.appName || "Draft Application").trim() || "Draft Application",
    base_url: baseUrl,
    rate_limit_rpm: 60,
    ssl_verify: true,
    auth_configs: authConfigs,
    endpoints: mappedEndpoints,
    collection: null,
  };

  return { app_config };
}
